import BreadCrumbsWeb from 'components/organisms/breadCrumbsWeb';
import NavigationHeaderWebTabs from 'components/organisms/navigationHeaderWebTabs/index.web';
import NavigationHeaderWeb from 'components/organisms/navigationWeb';
import {useTheme} from 'contexts/ThemeContext';
import {Flows, ScreenName} from 'navigations/Screens';
import {createNavigationAnalyticsInfo} from 'navigations/TabBar';
import React, {useEffect, useState, useTransition} from 'react';
import {View, ScrollView} from 'react-native';
import './chartsStyles.css';
import ChartTooltip from 'components/molecules/chartTooltip';
import AttentionChart from 'components/organisms/attentionChart/index.web';
import {CategoricalChartState} from 'recharts/types/chart/generateCategoricalChart';
import strings from 'translations/index.web';
import PickerButton from 'components/molecules/pickerButton/index.web';
import {useAnalyticsStore, useLoaderStore} from 'contexts/StoreContext';
import {AnalyticsScreenProps} from 'navigations/AnalyticsFlow';
import YAxisComponent from 'components/atoms/customYAxis';
import CustomChart from 'components/organisms/customChart';
import {observer} from 'mobx-react';
import styleSheet from './styles';
import {useNavigation} from '@react-navigation/native';

export enum FilterBarChartsType {
  attention = 'Attention',
  speakers = 'Speakers',
  reactions = 'Reaction',
}

const ChartsScreen = observer((props: AnalyticsScreenProps) => {
  const theme = useTheme();
  const styles = styleSheet(theme);
  const analyticsStore = useAnalyticsStore();
  const {
    studentData,
    analyticType,
    duration,
    mappedAttentionChartData,
    attentionAnalyticsColors,
    transcriptionsStudentData,
    reactionStudentData,
  } = analyticsStore;

  const loaderStore = useLoaderStore();

  const [payloadLegend, setPayloadLegend] = useState<any[]>([]);
  const [labelLegend, setLabelLegend] = useState<string>('');
  const [isLegendActive, setIsLegendActive] = useState(false);
  const [filterValue, setFilterValue] = useState(FilterBarChartsType.attention);

  const {addListener} = useNavigation();

  useEffect(() => {
    const unsubscribe = addListener('focus', async () => {
      switch (filterValue) {
        case FilterBarChartsType.speakers:
          await analyticsStore.getTranscriptionAnalytics(
            props.route.params?.id,
          );
          break;
        case FilterBarChartsType.reactions:
          await analyticsStore.getReactionAnalatytics(props.route.params?.id);
          break;
        case FilterBarChartsType.attention:
          await analyticsStore.getAttentionAnalatytics(props.route.params?.id);
          break;
      }
    });

    return unsubscribe;
  }, [addListener, filterValue]);

  useEffect(() => {
    const setChartTypeRequest = async () => {
      switch (filterValue) {
        case FilterBarChartsType.speakers:
          await analyticsStore.getTranscriptionAnalytics(
            props.route.params?.id,
          );
          break;
        case FilterBarChartsType.reactions:
          await analyticsStore.getReactionAnalatytics(props.route.params?.id);
          break;
        case FilterBarChartsType.attention:
          await analyticsStore.getAttentionAnalatytics(props.route.params?.id);
          break;
      }
    };

    setChartTypeRequest();
  }, [filterValue]);

  useEffect(() => {
    return () => {
      analyticsStore.clearChartsData();
    };
  }, []);

  const tickValues =
    filterValue === FilterBarChartsType.attention
      ? [...Array(duration + 1).keys()]
      : [...Array(duration * 60 + 1).keys()];

  let attentionChartWidth = 33 * tickValues.length;

  const handleChartClick = (event: CategoricalChartState) => {
    event.activePayload && setPayloadLegend(event.activePayload);
    event.activeLabel && setLabelLegend(event.activeLabel);
    setIsLegendActive(true);
  };

  const handlePickerValueChange = (value: string) => {
    setIsLegendActive(false);

    let filterType = Object.values(FilterBarChartsType)[
      Object.values(FilterBarChartsType).indexOf(value as FilterBarChartsType)
    ] as FilterBarChartsType;
    setFilterValue(filterType);
    analyticsStore.setAnalyticType(value);
  };

  let stack = createNavigationAnalyticsInfo(theme);

  const LeftView = (
    <View style={styles.leftContainer}>
      <PickerButton
        data={[
          strings.analyticsScreen.attention,
          strings.analyticsScreen.speakers,
          strings.analyticsScreen.reaction,
        ]}
        onValueChange={value => handlePickerValueChange(value)}
        onPress={() => {}}
        icon={theme.images.charts}
        pickerValue={filterValue}
        downArowIcon={true}
        pickerLabel={''}
        pickerContainerStyle={styles.pickerContainerStyle}
        pickerButtonStyle={styles.pickerContainerStyle}
        dropDownListStyle={styles.dropDownListStyle}
        zIndex={{zIndex: 1}}
      />
    </View>
  );

  const AttentionView = (
    <View style={styles.attentionView}>
      <View style={styles.attentionWrapper}>
        <YAxisComponent />
        <ScrollView
          style={styles.scrollViewStyle}
          contentContainerStyle={styles.scrollViewStyle}
          horizontal
          showsHorizontalScrollIndicator={true}>
          <View
            style={[styles.scrollViewContent, {width: attentionChartWidth}]}>
            <AttentionChart
              data={mappedAttentionChartData}
              colors={attentionAnalyticsColors}
              onChartClick={event => handleChartClick(event)}
              students={studentData}
              onAnimationEnd={() => loaderStore.stopLoading()}
            />
          </View>
        </ScrollView>
      </View>
      <View style={styles.tooltipView}>
        <ChartTooltip
          payload={payloadLegend}
          label={labelLegend}
          active={isLegendActive}
          colors={attentionAnalyticsColors}
          backgroundColor={theme.colors.dirtyWhite}
          students={studentData}
        />
      </View>
    </View>
  );

  const SpeakersReactionsChart = (
    <View style={styles.chartView}>
      <CustomChart
        headerTitle={analyticType}
        onPressHeaderButton={() => {}}
        onPressStudenListItem={() => {}}
        transcriptionsStudentData={transcriptionsStudentData}
        reactionStudentData={reactionStudentData}
        tickValueArray={tickValues}
        chartType={analyticType}
      />
    </View>
  );

  const renderNewChart = (): JSX.Element => {
    switch (filterValue) {
      case FilterBarChartsType.attention:
        return AttentionView;
      case FilterBarChartsType.reactions:
      case FilterBarChartsType.speakers:
        return SpeakersReactionsChart;
    }
  };

  return (
    <ScrollView style={styles.container}>
      <NavigationHeaderWeb activeName={Flows.analyticsFlow} />
      <BreadCrumbsWeb />

      <NavigationHeaderWebTabs
        navigationStack={stack}
        activeName={ScreenName.charts}
        sessionId={props.route.params?.id}
      />
      <View style={styles.chart}>
        {LeftView}
        {renderNewChart()}
      </View>
    </ScrollView>
  );
});

export default ChartsScreen;
